import { useEffect } from "react";
import { useAuth } from "@clerk/react";
import { clearPersistedBranding } from "@/lib/workspace-branding-storage";
import { clearWorkspaceAccent } from "@/lib/workspace-branding";

/**
 * Reverts branding as soon as Clerk reports a definitively signed-out session.
 *
 * Mounted at the ROOT, outside RequireAuth. The previous version of this check
 * lived inside the branding controller, which sits inside RequireAuth, and
 * RequireAuth redirects on !isSignedIn — so the controller was always unmounted
 * before the effect could observe a signed-out state. It was unreachable code.
 *
 * useAuth().isLoaded is what makes this safe: it is true only once Clerk has
 * finished restoring the session, so `isLoaded && !isSignedIn` is a real
 * sign-out and not the transient null that App.tsx's cache invalidator has to
 * guard against.
 *
 * Exactly one of this and WorkspaceAccentController is ever active. Renders
 * nothing.
 */
export function BrandingSessionSentinel(): null {
  const { isLoaded, isSignedIn } = useAuth();

  useEffect(() => {
    if (isLoaded && !isSignedIn) {
      clearWorkspaceAccent();
      clearPersistedBranding();
    }
  }, [isLoaded, isSignedIn]);

  return null;
}
